/**
 * 자동 재시도 유틸리티 (지수 백오프)
 */

export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, "shouldRetry" | "onRetry">> = {
  maxRetries: 3,
  baseDelayMs: 500,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
};

/**
 * 지수 백오프 지연 시간 계산
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  backoffMultiplier: number
): number {
  const delay = baseDelayMs * Math.pow(backoffMultiplier, attempt);
  // jitter 추가 (±10%)
  const jitter = delay * 0.1 * (Math.random() * 2 - 1);
  return Math.min(delay + jitter, maxDelayMs);
}

/**
 * 지연 함수
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 네트워크 오류인지 확인
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes("network") ||
      message.includes("fetch") ||
      message.includes("timeout") ||
      message.includes("connection") ||
      message.includes("offline") ||
      error.name === "NetworkError" ||
      error.name === "AbortError"
    );
  }
  return false;
}

/**
 * 재시도 가능한 오류인지 확인
 */
function isRetryableError(error: unknown): boolean {
  // 네트워크 오류는 재시도
  if (isNetworkError(error)) {
    return true;
  }

  // GraphQL 에러 중 특정 코드는 재시도하지 않음
  if (error && typeof error === "object" && "graphQLErrors" in error) {
    const graphQLErrors = (error as { graphQLErrors: Array<{ extensions?: { code?: string } }> })
      .graphQLErrors;
    for (const gqlError of graphQLErrors) {
      const code = gqlError.extensions?.code;
      // 버전 충돌, 인증 오류 등은 재시도하지 않음
      if (code === "VERSION_CONFLICT" || code === "UNAUTHENTICATED" || code === "FORBIDDEN") {
        return false;
      }
    }
  }

  return true;
}

/**
 * 지수 백오프로 함수 재시도
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const { maxRetries, baseDelayMs, maxDelayMs, backoffMultiplier } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };
  const shouldRetry = options.shouldRetry ?? isRetryableError;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // 마지막 시도이거나 재시도 불가능한 오류면 throw
      if (attempt === maxRetries || !shouldRetry(error, attempt)) {
        throw error;
      }

      // 백오프 지연
      const delayMs = calculateBackoffDelay(attempt, baseDelayMs, maxDelayMs, backoffMultiplier);

      // 재시도 콜백 호출
      options.onRetry?.(error, attempt + 1, delayMs);

      await sleep(delayMs);
    }
  }

  throw lastError;
}

/**
 * 재시도 상태 추적
 */
export interface RetryState {
  attempt: number;
  maxAttempts: number;
  nextRetryAt: Date | null;
  lastError: string | null;
}

export function createRetryState(maxRetries: number): RetryState {
  return {
    attempt: 0,
    maxAttempts: maxRetries,
    nextRetryAt: null,
    lastError: null,
  };
}

export function updateRetryState(state: RetryState, error: unknown, delayMs: number): RetryState {
  return {
    ...state,
    attempt: state.attempt + 1,
    nextRetryAt: new Date(Date.now() + delayMs),
    lastError: error instanceof Error ? error.message : String(error),
  };
}
